import { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { SERVICES, TECH, PROCESS, POSTS, FAQS, INDUSTRIES } from '../data';
import { IMG, u } from '../images';
import { refreshScroll } from '../lib/useScrollAnimations';

// Reusable building blocks shared by several pages.

// Renders a heading two-tone: the second-to-last word solid colour, the last word hollow (outlined).
export function HollowTitle({ children, dark = false }) {
  if (typeof children !== 'string') return children;
  const words = children.split(' ');
  if (words.length < 2) return children;
  const last = words.pop();
  const mid = words.length > 1 ? words.pop() : null;
  return (
    <>
      {words.join(' ')}{words.length ? ' ' : ''}
      {mid && <><span className={dark ? 'text-accent-light' : 'text-accent'}>{mid}</span>{' '}</>}
      <span className={dark ? 'text-outline-light' : 'text-outline'}>{last}</span>
    </>
  );
}

export function SectionHead({ eyebrow, title, text, dark = false, center = true }) {
  return (
    <div className={`max-w-2xl ${center ? 'mx-auto text-center' : ''}`}>
      <span className={`eyebrow ${dark ? 'eyebrow-dark' : ''}`} data-anim="down">{eyebrow}</span>
      <h2 className={`h2 mt-5 ${dark ? 'text-white' : 'text-ink'}`} data-split><HollowTitle dark={dark}>{title}</HollowTitle></h2>
      {text && <p className={`mt-4 text-lg ${dark ? 'text-white/65' : 'text-slate-600'}`} data-anim="up">{text}</p>}
    </div>
  );
}

export function ServiceGrid({ limit }) {
  const list = limit ? SERVICES.slice(0, limit) : SERVICES;
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-anim="up" data-stagger>
      {list.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} className="svc-card lift group relative flex flex-col overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-sm transition-shadow duration-300 hover:shadow-2xl">
          <div className="relative h-40 shrink-0 overflow-hidden">
            <img src={u(IMG.service[s.slug], 600)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-brand/10" />
            <span className="svc-ico absolute bottom-3 left-5 grid h-12 w-12 place-items-center rounded-2xl bg-brand-lime text-ink shadow-lg transition-all duration-300">
              <Icon name={s.icon} className="h-6 w-6" />
            </span>
          </div>
          <div className="flex flex-1 flex-col p-6">
          <h3 className="mt-1 text-xl font-bold text-ink transition-colors">{s.title}</h3>
          <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600 transition-colors">{s.desc}</p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {s.tags.map((t) => (
              <li key={t} className="rounded-full bg-brand-soft px-3 py-1 text-xs font-medium text-brand transition-colors group-hover:bg-white/15 group-hover:text-white">{t}</li>
            ))}
          </ul>
          <span className="svc-more mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand transition-colors">
            Learn more <Icon name="arrow" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function TechMarquee() {
  return (
    <div data-anim="fade">
      <p className="text-center text-sm font-semibold uppercase tracking-widest text-slate-500">Technologies we work with</p>
      <div className="relative mt-6 overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
        <div className="flex w-max animate-marquee gap-4">
          {[...TECH, ...TECH].map((t, i) => (
            <span key={i} className="rounded-full border border-brand/15 bg-white px-6 py-3 font-display text-sm font-semibold text-brand shadow-sm">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProcessTimeline() {
  return (
    <ol className="relative space-y-8" data-line-wrap>
      <span className="absolute bottom-6 left-[27px] top-6 w-0.5 bg-brand/10" aria-hidden="true" />
      <span data-line className="absolute bottom-6 left-[27px] top-6 w-0.5 bg-gradient-to-b from-brand-soft to-brand" aria-hidden="true" />
      {PROCESS.map((s, i) => (
        <li key={s.n} className="relative flex gap-6" data-anim={i % 2 ? 'right' : 'left'}>
          <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full bg-brand font-display text-lg font-bold text-white ring-8 ring-white">{s.n}</span>
          <div className="lift flex-1 rounded-3xl border border-brand/10 bg-brand-soft/60 p-6 transition-shadow hover:shadow-xl">
            <h3 className="text-xl font-bold text-ink">{s.title}</h3>
            <p className="mt-2 leading-relaxed text-slate-600">{s.desc}</p>
            {s.points && (
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-slate-700"><Icon name="check" className="h-4 w-4 text-brand" strokeWidth={2.6} />{p}</li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function PostCard({ post }) {
  const idx = Math.max(0, POSTS.indexOf(post));
  return (
    <Link to={`/blog/${post.slug}`} className="group flex flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-brand/10 transition-shadow duration-300 hover:shadow-2xl">
      <div className="relative h-44 overflow-hidden bg-ink">
        <img src={u(IMG.post[idx % IMG.post.length], 700)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-brand">{post.cat}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs text-slate-500">{post.date} · {post.read}</p>
        <h3 className="mt-2 text-lg font-bold leading-snug text-ink">{post.title}</h3>
        <p className="mt-2 flex-1 text-[15px] leading-relaxed text-slate-600">{post.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand">Read article <Icon name="arrow" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
}

export function LatestPosts({ count = 3 }) {
  return (
    <div className="grid gap-7 md:grid-cols-3" data-anim="up" data-stagger>
      {POSTS.slice(0, count).map((p) => <PostCard key={p.slug} post={p} />)}
    </div>
  );
}

export function IndustryChips() {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-anim="up" data-stagger>
      {INDUSTRIES.map((i) => (
        <div key={i} className="group relative aspect-[4/5] overflow-hidden rounded-3xl shadow-sm">
          <img src={u(IMG.industry[i], 700)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-brand/10 transition-colors group-hover:from-brand-dark/90" />
          <p className="absolute inset-x-5 bottom-5 font-display text-lg font-bold leading-tight text-white">{i}<span className="mt-2 block h-0.5 w-8 bg-brand-lime transition-all duration-300 group-hover:w-16" /></p>
        </div>
      ))}
    </div>
  );
}

export function FaqList({ items = FAQS }) {
  const [open, setOpen] = useState(0);
  const toggle = (idx) => { setOpen(open === idx ? -1 : idx); refreshScroll(); };
  return (
    <div className="space-y-4" data-anim="right" data-stagger>
      {items.map((f, idx) => (
        <div key={f.q} className={`rounded-2xl border transition-colors ${open === idx ? 'border-brand/40 bg-brand-soft' : 'border-brand/10 bg-white hover:border-brand/30'}`}>
          <button onClick={() => toggle(idx)} aria-expanded={open === idx} className="flex w-full items-center justify-between gap-4 p-5 text-left font-display text-lg font-semibold text-ink">
            {f.q}
            <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-white transition-transform duration-300 ${open === idx ? 'rotate-45' : ''}`}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            </span>
          </button>
          <div className={`grid transition-all duration-300 ${open === idx ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <p className="overflow-hidden px-5 leading-relaxed text-slate-600" style={{ paddingBottom: open === idx ? '1.25rem' : 0 }}>{f.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
